import { examMetadataModule } from '@/store/examMetadata/module'
import { runCloudFunction } from '@/store/parseUtils'
import { callApi } from '@/store/apiUtils'
import { quizModule } from '@/store/quiz/module'
import type { TActiveQuiz, TQuizMode } from '@/store/quiz/state'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import type { Study, API } from '@pocketprep/types'
import { userModule } from '@/store/user/module'
import { localISODateString, generateAnswerSeeds } from '@/utils'
import { fetchLoadable } from '@/store/utils'
import { globalQuestionMetricModule } from '@/store/globalQuestionMetric/module'
import { utils } from '@pocketprep/ui-kit'
import { mockExamModule } from '@/store/mockExam/module'
import { analyticsModule } from '@/store/analytics/module'
import { assignmentModule } from '@/store/assignment/module'

const fetchAnsweredQuestions = async (quizId?: string) => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])

    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()

    if (!currentExam) {
        return
    }

    // if we're fetching a specific quiz's answers we just want to tack them onto the state
    if (quizId) {
        const answeredQuestions = await runCloudFunction<Study.Cloud.fetchQuestionsByQuizIdsV3>(
            'fetchQuestionsByQuizIds-v3',
            {
                quizIds: [ quizId ],
                examMetadataId: currentExam.objectId,
                compositeKey: currentExam?.compositeKey,
            }
        )

        // use a Set to dedupe just in case
        const questionsSerialObj =
            ([ ...await quizModule.state.answeredQuestions.value, ...answeredQuestions ])
                .reduce((acc, question) => {
                    acc[question.serial] = question
                    return acc
                }, {} as { [serial: string]: Study.Class.QuestionJSON })

        quizModule.state.answeredQuestions.value = Object.values(questionsSerialObj)

        return answeredQuestions
    } else {
        const quizIds = quizModule.state.quizzes.map(q => q.objectId)
        await fetchLoadable(
            quizModule.state.answeredQuestions,
            () => runCloudFunction<Study.Cloud.fetchQuestionsByQuizIdsV3>(
                'fetchQuestionsByQuizIds-v3',
                {
                    quizIds,
                    compositeKey: currentExam?.compositeKey,
                }
            )
        )
    }
}

export type TCreateActiveQuizParams = {
    prebuiltQuizId?: string
    mode: TQuizMode
    questionCount?: number
    timeLimit?: number  // minutes
    showAnswersAtEnd?: boolean
    showCheckAnswerButton?: boolean
    level?: number
    numCorrectToLevelUp?: number
    subjectNames?: string[]
    subjectIds?: string[]
    include?: {
        new?: boolean
        answered?: boolean
        flagged?: boolean
        incorrect?: boolean
    }
}

const createActiveQuiz = async (params: TCreateActiveQuizParams) => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    const currentUserExamMetadata = userExamMetadataModule.getters.getCurrentUserExamMetadata()

    if (!currentExam) {
        throw new Error('No current exam found')
    }
    const examGuid = currentExam.examGuid
    const examMetadataId = currentExam.objectId
    const currentExamMajorVersion = currentExam.version.split('.')[0]
    if (!currentExamMajorVersion) {
        throw new Error('quiz/action - CreateActiveExam: no current exam version')
    }

    let quizQuestions: Study.Class.QuestionJSON[]
    let prebuiltQuiz: Study.Class.PrebuiltQuizJSON | null = null

    if (params.prebuiltQuizId) {
        prebuiltQuiz = assignmentModule.getters.getAssignment()
        quizQuestions = assignmentModule.getters.getAssignmentQuestions()

        if (prebuiltQuiz && quizQuestions) {
            const prebuiltQuizExam = prebuiltQuiz.examMetadata as Study.Class.ExamMetadataJSON
            // if prebuilt quiz is in exam you aren't actively studying, update currently studying
            if (currentExam.objectId !== prebuiltQuizExam.objectId) {
                await userModule.actions.updateCurrentExam(prebuiltQuizExam.objectId)
            }


            // check if active quiz is already the prebuilt quiz with
            // the same objectId as prebuiltQuiz objectId and, if so, just return
            const activeQuiz = quizModule.getters.getActiveQuiz()
            if (
                activeQuiz?.prebuiltQuiz
                && activeQuiz?.prebuiltQuiz.objectId === prebuiltQuiz.objectId
            ) {
                return
            }
        } else {
            // if fetch quiz returns a quiz object, then just return it and dont start a quiz
            return assignmentModule.getters.getCompletedAssignment()
        }
    } else if (params.mode === 'levelUp' && params.subjectIds && params.level) {
        const fetchQuizParams: Parameters<Study.Cloud.fetchLevelUpQuizQuestionsV2>[0] = {
            examGuid,
            examMetadataId,
            majorVersion: currentExamMajorVersion,
            questionCount: params.questionCount,
            subjectName: params.subjectNames?.[0],
            subjectId: params.subjectIds?.[0],
            level: params.level,
            includeMatrixQuestions: true,
            includeMPMCQuestions: true,
            includeBuildListQuestions: true,
        }

        // If Level Up progress has been reset, include the most recent reset date in fetch params
        if (currentUserExamMetadata?.levelUpResetDates?.length) {
            const lastResetDateISO = currentUserExamMetadata.levelUpResetDates
                .sort((a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime())[0].iso
            fetchQuizParams.lastResetDateISO = lastResetDateISO
        }

        quizQuestions = await runCloudFunction<Study.Cloud.fetchLevelUpQuizQuestionsV3>(
            'fetchLevelUpQuizQuestions-v3',
            fetchQuizParams
        )
    } else if (params.mode === 'recallChallenge') {
        quizQuestions = await runCloudFunction<Study.Cloud.fetchRecallQuizQuestions>('fetchRecallQuizQuestions', {
            examMetadataId,
            includeMPMCQuestions: true,
            includeBuildListQuestions: true,
        })
    } else {
        const fetchQuizParams: Parameters<Study.Cloud.fetchQuizQuestionsV4>[0] = {
            examGuid,
            examMetadataId,
            majorVersion: currentExamMajorVersion,
            questionCount: params.questionCount || 10,
            includeMatrixQuestions: true,
            includeMPMCQuestions: true,
            includeBuildListQuestions: true,
        }
        if (params.subjectIds?.length) {
            fetchQuizParams.subjectIds = params.subjectIds
        } else if (params.subjectNames?.length) {
            fetchQuizParams.subjects = params.subjectNames
        } else if (currentUserExamMetadata?.disabledSubjects?.length) {
            // If the user has any disabled subjects, pass the enabled subjects to fetchQuizQuestions
            const allSubjects = Object.values(currentExam.knowledgeAreas || []).map(ka => ka.name)
            const disabledSubjectsSet = new Set(currentUserExamMetadata.disabledSubjects)
            const enabledSubjects = allSubjects.filter(subject => !disabledSubjectsSet.has(subject))
            fetchQuizParams.subjects = enabledSubjects
        }
        const include = params.include
        if (include && Object.keys(include).length) {
            fetchQuizParams.include = include
        }

        quizQuestions = await runCloudFunction<Study.Cloud.fetchQuizQuestionsV5>(
            'fetchQuizQuestions-v5',
            fetchQuizParams
        )
    }
    // generate random answer seed
    const answerSeeds = generateAnswerSeeds(quizQuestions)
    const currentUser = userModule.state.user
    // Store the active quiz in our store state and in local storage
    const activeQuiz: TActiveQuiz = {
        questions: quizQuestions,
        answers: [],
        examGuid,
        mode: params.mode,
        startedAt: localISODateString(),  // includes timezone info
        currentQuestionIndex: 0,
        durationSeconds: 0,
        breakDurationSeconds: 0,
        timerType: 'exam',
        answerSeeds,
        userId: currentUser?.objectId,
    }
    if (prebuiltQuiz) {
        activeQuiz.prebuiltQuiz = prebuiltQuiz
    }
    if (params.timeLimit) {
        activeQuiz.timeLimit = params.timeLimit // minutes
    }
    if ('showAnswersAtEnd' in params && params.mode !== 'levelUp') {
        activeQuiz.showAnswersAtEnd = params.showAnswersAtEnd
    }
    if ('showCheckAnswerButton' in params && params.mode !== 'levelUp') {
        activeQuiz.showCheckAnswerButton = params.showCheckAnswerButton
    }
    if (params.numCorrectToLevelUp) {
        activeQuiz.numCorrectToLevelUp = params.numCorrectToLevelUp
    }

    // Clear out any entries for user explanations or previous AI feedback
    localStorage.removeItem('levelUpQuestionExperiment_explanationAnswer')
    localStorage.removeItem('levelUpQuestionExperiment_aiFeedback')
    if (localStorage.getItem('tutorNudgesShownForQuestionIds')) {
        localStorage.removeItem('tutorNudgesShownForQuestionIds')
    }

    updateActiveQuiz(activeQuiz)
}

const updateActiveQuiz = (activeQuiz: TActiveQuiz | null) => {
    if (activeQuiz) {
        // Reset activeMockExamQuiz when setting activeQuiz
        mockExamModule.actions.updateActiveMockExamQuiz(null)
    }
    quizModule.state.activeQuiz = activeQuiz

    try {
        localStorage.setItem('activeQuiz', JSON.stringify(activeQuiz))
    } catch (err) {
        const isQuotaExceeded = err instanceof DOMException
            && (err.name === 'QuotaExceededError')
            && localStorage.length !== 0

        if (!isQuotaExceeded) {
            throw err
        }
    }
}

const recordQuiz = async (params: Parameters<Study.Cloud.recordQuiz>[0]) => {
    // Remove checked property from answers (only needed for active quiz state)
    params.answers.forEach(ans => {
        const ansWithChecked = ans as typeof ans & { checked?: boolean }
        delete ansWithChecked.checked
    })
    params.includeRecommendedNext = true

    if (localStorage.getItem('quizResultsFeedback')) {
        localStorage.removeItem('quizResultsFeedback')
    }

    const recordQuizResponse = await runCloudFunction<Study.Cloud.recordQuiz>('recordQuiz', params)

    // add answered questions to store
    const quizQuestions = quizModule.state.activeQuiz?.questions || []
    const answeredSerials = params.answers.map(a => a.questionSerial)
    const answeredQuestions = quizQuestions.filter(q => answeredSerials.includes(q.serial))
    const questionsSerialObj =
        ([ ...await quizModule.state.answeredQuestions.value, ...answeredQuestions ])
            .reduce((acc, question) => {
                acc[question.serial] = question
                return acc
            }, {} as { [serial: string]: Study.Class.QuestionJSON })

    quizModule.state.answeredQuestions.value = Object.values(questionsSerialObj)
    quizModule.state.recommendedNext = recordQuizResponse.recommendedNext
        ? {
            quizId: recordQuizResponse.quiz.objectId,
            ...recordQuizResponse.recommendedNext,
        }
        : null
    localStorage.setItem('recommendedNext', JSON.stringify(quizModule.state.recommendedNext))

    if (recordQuizResponse.feedback) {
        localStorage.setItem('quizResultsFeedback', JSON.stringify(recordQuizResponse.feedback))
    }

    // update quiz and global metrics in store
    quizModule.state.quizzes.push(recordQuizResponse.quiz)
    quizModule.actions.updateActiveQuiz(null)
    assignmentModule.actions.resetAssignmentState()

    await Promise.all([
        globalQuestionMetricModule.actions.fetchGlobalQuestionMetrics(true),
        userExamMetadataModule.actions.fetchUserExamMetadata(true),
        userModule.actions.fetchUserData({ userData: true }),
    ])

    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    if (currentExam) {
        await analyticsModule.actions.googleAnalyticsTrack('Quiz Submission', {
            'event_category': utils.studyModes[params.mode as -1 | 0 | 2 | 3 | 4 | 5 | 6 | 10].name,
            'event_label': currentExam.nativeAppName,
        })
    }

    if (params.mode === 6) {
        await analyticsModule.actions.trackEvent('Level Up Quiz Completed')
    }

    analyticsModule.actions.updateIntercom()

    return recordQuizResponse
}

export type generateExplainYourAnswerFeedback = (params: {
    userExplanation: string
    userSelectedAnswer: string
    serial: string
    examMetadataId: string
}) => {
    success: boolean
    feedback: string
}
const generateExplainYourAnswerFeedback = async (params: Parameters<generateExplainYourAnswerFeedback>[0]) => {
    return runCloudFunction<generateExplainYourAnswerFeedback>('generateExplainYourAnswerFeedback', params)
}

type ParseTIsLevelUpEnabled = (params: { examGuid?: string }) => boolean
const isLevelUpEnabled = async (params: { examGuid?: string }) => {
    const nestApiDomain = import.meta.env.VUE_APP_NEST_API_URL_DOMAIN
    if (!nestApiDomain) {
        return runCloudFunction<ParseTIsLevelUpEnabled>('isLevelUpEnabled', params)
    }
    const response: API.CheckLevelUpEligibilityResponse = await callApi({
        method: 'GET',
        url: '/level-up/eligibility',
        params,
    })
    return response.eligible
}

const fetchConceptQuizQuestions = async (params: Parameters<Study.Cloud.fetchConceptQuizQuestions>[0]) =>
    runCloudFunction<Study.Cloud.fetchConceptQuizQuestions>('fetchConceptQuizQuestions', params)

const clearRecommendedNext = () => {
    quizModule.state.recommendedNext = null
    localStorage.removeItem('recommendedNext')
}

export default {
    createActiveQuiz,
    updateActiveQuiz,
    fetchAnsweredQuestions,
    recordQuiz,
    isLevelUpEnabled,
    generateExplainYourAnswerFeedback,
    fetchConceptQuizQuestions,
    clearRecommendedNext,
}
